import { html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import d3 from './lib/d3';

declare const salaryChartLabels:{
  location: string;
  jobTitle: string;
  yourSalary: string;
  lowSalary: string;
  medianSalary: string;
  highSalary: string;
};

const tag = 'check-salary-form-data-visualizer';

@customElement(tag)
export class DataVisualizer extends LitElement {
  // Allows for importing the module and telling the build tools a side effect occurred. Goal: Reduce filesize if not in use.
  enabled = true;

  // Exposed properties
  @property({ type: Object }) data;

  @property() type = 'compare';

  @property() country = '';

  @property() market = '';

  @property({ type: String, attribute: 'job-title-name' }) jobTitleName = '';

  @property({ type: Number }) salary = null;

  // Forces the removal of the Shadow DOM for global style usage
  protected createRenderRoot(): Element | ShadowRoot {
    return this;
  }

  connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener('resize', () => this.generateChart(), false);
    window.addEventListener('orientationchange', () => this.generateChart(), false);
  }

  protected firstUpdated(): void {
    this.generateChart();
  }

  getCurrencySymbol(): string {
    const currencySymbols: Record<string, string> = {
      US: '$',
      CA: '$',
      UK: '£',
      NL: '€',
      DE: '€',
      FR: '€',
      AU: '$',
      JP: '¥',
    };
    return currencySymbols[this.country] || '$'; // Default to USD if country not found
  }

  generateChart() {
    const margin = {
      top: 20, right: 50, bottom: 50, left: 70,
    };
    const container = d3.select('#salary-data-viz');
    container.selectAll('svg').remove();
    const width = parseInt(container.style('width')) - margin.left - margin.right;
    const height = 300;
    const currencySymbol = this.getCurrencySymbol();
    const { low, median, high } = this.data;
    const data = [
      { name: salaryChartLabels.highSalary, value: high, color: '#FF9D6E' },
      { name: salaryChartLabels.medianSalary, value: median, color: '#5D3754' },
      { name: salaryChartLabels.lowSalary, value: low, color: '#41748D' },
    ];
    if (this.type === 'compare' && this.salary > 0) {
      data.push({ name: salaryChartLabels.yourSalary, value: this.salary, color: '#E53935' });
    }
    const svg = container.append('svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);
    // Scale up to 250,000 max for larger salaries
    const maxSalary = Math.max(250000, d3.max(data, (d) => d.value));
    const x = d3.scaleLinear()
      .domain([0, maxSalary])
      .range([0, width]);
    const y = d3.scaleBand()
      .domain(data.map((d) => d.name))
      .range([0, height])
      .padding(0.5);
    // Gridlines (light grey, dotted)
    svg.append('g')
      .attr('class', 'x-grid')
      .attr('transform', `translate(0,${height})`)
      .call(
        d3.axisBottom(x)
          .ticks(6)
          .tickSize(-height)
          .tickFormat((d) => `${currencySymbol}${d3.format(',.0f')(d)}`),
      )
      .call((g) => g.select('.domain').remove())
      .selectAll('line')
      .style('stroke', '#aaa')
      .style('stroke-dasharray', '3,3');
    svg.selectAll('.x-grid text')
      .style('fill', '#1A1919')
      .style('font-size', '13px')
      .style('font-weight', '500');
    // Y axis
    svg.append('g')
      .attr('class', 'y-axis')
      .call(d3.axisLeft(y).tickSize(0))
      .selectAll('text')
      .style('fill', '#1a1919')
      .style('font-size', '14px')
      .style('font-weight', '500');
    // Solid color bars (no gradient, no rounded corners)
    svg.selectAll('.bar')
      .data(data)
      .enter()
      .append('rect')
      .attr('class', 'bar')
      .attr('y', (d) => y(d.name))
      .attr('height', y.bandwidth() - 2)
      .attr('x', 0)
      .attr('width', (d) => x(d.value))
      .style('fill', (d) => d.color)
      .style('stroke', '#000') // black outline
      .style('stroke-width', 1)
      .style('rx', 0);
    // Value labels (black)
    svg.selectAll('.label')
      .data(data)
      .enter()
      .append('text')
      .attr('x', (d) => x(d.value) + 8)
      .attr('y', (d) => y(d.name) + y.bandwidth() / 2 + 4)
      .text((d) => `${currencySymbol}${d3.format(',.0f')(d.value)}`)
      .style('font-size', '14px')
      .style('fill', '#1a1919')
      .style('font-weight', '500');
    // Add black baseline (horizontal axis)
    svg.append('line')
      .attr('x1', 0)
      .attr('x2', width)
      .attr('y1', height)
      .attr('y2', height)
      .style('stroke', '#1a1919')
      .style('stroke-width', 1.5);
    //  Add vertical axis baseline
    svg.append('line')
      .attr('x1', 0)
      .attr('x2', 0)
      .attr('y1', 0)
      .attr('y2', height)
      .style('stroke', '#1a1919')
      .style('stroke-width', 1.5);
  }

  render() {
    return html`
      <div id="data-viz-wrapper" class=${classMap({ 'data-viz-wrapper': true, shorter: !this.salary })}>
        <figure id="salary-data-viz" class="salary-data-viz shorter">
          <figcaption class="show-for-sr">
            <table>
              <thead>
                <tr>
                  <th>${salaryChartLabels.location}</th>
                    <!-- ## TODO -- we will need an API call to support this ... -->
                   <!-- <th class="job-title-th">37 Salary Results for CX Lead</th> -->
                   <th class="job-title-th">${salaryChartLabels.jobTitle}</th>
                   ${this.type === 'compare' ? html`<th>${salaryChartLabels.yourSalary}</th>` : html``}
                  <th>${salaryChartLabels.lowSalary}</th>
                  <th>${salaryChartLabels.medianSalary}</th>
                  <th>${salaryChartLabels.highSalary}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="user-location">${this.market ? this.market : this.country}</td>
                  <td class="job-title">${this.jobTitleName}</td>
                  ${this.type === 'compare' ? html`<td class="user-salary">${this.salary}</td>` : html``}
                  <td class="low-salary">${this.data.low}</td>
                  <td class="median-salary">${this.data.median}</td>
                  <td class="high-salary">${this.data.high}</td>
                </tr>
              </tbody>
            </table>
          </figcaption>
        </figure>
      </div>
    `;
  }
}
